import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CalendarDays, Clock, CalendarX2, ArrowRight } from "lucide-react";
import { api } from "@/lib/api";
import { useTitle, formatDate, formatTime, formatPrice, imageUrl } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { PageLoader, StatusBadge, EmptyState, DemoBadge } from "@/components/common";
import { CustomerActions } from "@/components/bookings/BookingActions";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

function BookingCard({ b, reload }) {
  return (
    <div className="flex flex-col gap-4 rounded-2xl border border-slate-200 bg-white p-5 sm:flex-row sm:items-center" data-testid={`booking-card-${b.id}`}>
      <img src={imageUrl(b.provider_image)} alt="" className="h-16 w-16 rounded-xl object-cover" />
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <StatusBadge status={b.status} testId={`booking-status-${b.id}`} />
          {b.is_demo && <DemoBadge />}
          {b.reschedule_request?.status === "pending" && <span className="rounded-full bg-sky-50 px-2 py-0.5 text-xs font-semibold text-sky-700">Reschedule requested</span>}
          {b.reschedule_request?.status === "declined" && <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs font-semibold text-slate-600">Reschedule declined</span>}
        </div>
        <p className="mt-2 font-heading text-lg font-semibold text-slate-900">{b.service_name}</p>
        <p className="text-sm text-slate-600">{b.provider_name}</p>
        <div className="mt-2 flex flex-wrap gap-4 text-sm text-slate-500">
          <span className="flex items-center gap-1"><CalendarDays className="h-4 w-4" />{formatDate(b.date)}</span>
          <span className="flex items-center gap-1"><Clock className="h-4 w-4" />{formatTime(b.start_time)}</span>
          <span className="font-medium text-slate-700">{formatPrice(b.price)}</span>
        </div>
      </div>
      <div className="flex flex-col gap-2 sm:items-end">
        <CustomerActions booking={b} onDone={reload} />
        <Link to={`/bookings/${b.id}`} data-testid={`view-booking-${b.id}`} className="inline-flex items-center gap-1 text-sm font-semibold text-sky-700">View booking <ArrowRight className="h-4 w-4" /></Link>
      </div>
    </div>
  );
}

export default function MyBookings() {
  useTitle("My Bookings");
  const { user } = useAuth();
  const [items, setItems] = useState(null);
  const load = useCallback(() => api.get("/bookings").then(({ data }) => setItems(data)), []);
  useEffect(() => { load(); }, [load]);
  if (!items) return <PageLoader />;
  const upcoming = items.filter((b) => ["pending", "confirmed"].includes(b.status) && !b.is_past);
  const past = items.filter((b) => b.status === "completed" || (b.status !== "cancelled" && b.is_past)).reverse();
  const cancelled = items.filter((b) => b.status === "cancelled").reverse();
  const tabs = [["upcoming", "Upcoming", upcoming], ["past", "Past", past], ["cancelled", "Cancelled", cancelled]];
  return (
    <div className="mx-auto max-w-4xl px-4 py-10 sm:px-6" data-testid="my-bookings-page">
      <h1 className="font-heading text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">My Bookings</h1>
      <p className="mt-2 text-slate-600">Hi {user.name.split(" ")[0]}, manage your appointments here.</p>
      <Tabs defaultValue="upcoming" className="mt-8">
        <TabsList className="grid h-12 w-full grid-cols-3 rounded-xl bg-slate-100 p-1">
          {tabs.map(([k, label, list]) => (
            <TabsTrigger key={k} value={k} data-testid={`tab-${k}`} className="h-10 rounded-lg text-sm font-semibold">{label} ({list.length})</TabsTrigger>
          ))}
        </TabsList>
        {tabs.map(([k, label, list]) => (
          <TabsContent key={k} value={k} className="mt-6 space-y-4" data-testid={`tab-content-${k}`}>
            {list.length === 0 ? (
              <EmptyState icon={CalendarX2} title={`No ${label.toLowerCase()} bookings`} text="When you book a service it will show up here."
                action={<Link to="/services" className="rounded-xl bg-sky-600 px-5 py-3 text-sm font-semibold text-white">Find a Service</Link>} />
            ) : list.map((b) => <BookingCard key={b.id} b={b} reload={load} />)}
          </TabsContent>
        ))}
      </Tabs>
    </div>
  );
}
